import { useCallback, useMemo } from "react";

import { useSearchParams } from "@remix-run/react";

import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { captureException } from "~/lib/analytics";
import { logger } from "~/lib/logger";

interface RequestParams {
    params?: Record<string, string | undefined | null>;
}

interface RequestOptions extends RequestInit {
    params?: Record<string, string | undefined | null>;
}

// AIDEV-NOTE: Retry configuration for transient network failures.
// These are network-level failures (DNS, timeouts, connectivity issues),
// NOT HTTP error responses (4xx, 5xx). See GitHub issue #241.
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

/**
 * Check if we have network connectivity.
 * Returns true if online or if navigator.onLine is not available (SSR, older browsers).
 */
function isNetworkAvailable(): boolean {
    return typeof navigator === "undefined" || navigator.onLine;
}

/**
 * Sleep for a given number of milliseconds.
 */
function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Determines if an error is a retryable network error.
 * Network errors (DNS failures, connection refused, timeouts) should be retried.
 * HTTP errors (4xx, 5xx) should NOT be retried at the fetch level.
 */
function isRetryableNetworkError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;

    const message = error.message.toLowerCase();
    // "Failed to fetch" is the standard message for network failures in browsers
    // "NetworkError" and "Network request failed" are variants
    // "AbortError" should NOT be retried (intentional cancellation)
    return (
        message.includes("failed to fetch") ||
        message.includes("networkerror") ||
        message.includes("network request failed") ||
        message.includes("load failed") // Safari variant
    );
}

// AIDEV-NOTE: This hook automatically prepends /api to all routes. When using apiClient,
// pass routes WITHOUT /api prefix (e.g., "/session" not "/api/session") to avoid /api/api/
export const useApiClient = () => {
    const { token: accessToken, loading } = useSupabaseSession();
    const [searchParams] = useSearchParams();
    const sessionToken = useMemo(
        () => searchParams.get("sessionToken"),
        [searchParams],
    );

    const buildUrl = useCallback(
        (
            baseUrl: string,
            params?: Record<string, string | null | undefined>,
        ) => {
            // Check if we're on the client to avoid SSR issues
            const baseUrlPrefix =
                typeof window !== "undefined"
                    ? window.location.protocol +
                      "//" +
                      window.location.host +
                      "/api"
                    : "/api";

            const url = new URL(
                baseUrlPrefix + baseUrl,
                typeof window !== "undefined"
                    ? window.location.origin
                    : "http://localhost:3000",
            );

            // TODO: this feels hacky to include sessionToken whenever we find it in the URL. Hmm...
            const paramsWithSessionToken = {
                ...params,
                sessionToken: sessionToken,
            };
            if (paramsWithSessionToken) {
                for (const [key, value] of Object.entries(
                    paramsWithSessionToken,
                )) {
                    if (value !== undefined && value !== null) {
                        url.searchParams.set(key, value);
                    }
                }
            }
            return url.toString();
        },
        [sessionToken],
    );

    const request = useCallback(
        async (url: string, options: RequestOptions) => {
            const finalUrl = buildUrl(url, options.params);

            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };

            if (accessToken) {
                headers.Authorization = `Bearer ${accessToken}`;
            }

            if (options.headers) {
                Object.assign(headers, options.headers);
            }

            // AIDEV-NOTE: Retry loop for transient network failures (GitHub issue #241).
            // Only retries on network-level errors (DNS, connectivity), not HTTP errors.
            let lastError: Error | null = null;

            for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
                // Check network connectivity before attempting fetch
                if (!isNetworkAvailable()) {
                    const offlineError = new Error(
                        `Network unavailable (navigator.onLine=false) for ${
                            options.method || "GET"
                        } ${url}`,
                    );
                    logger
                        .withContext({ url, method: options.method || "GET" })
                        .info("API request skipped: browser reports offline");
                    throw offlineError;
                }

                try {
                    const response = await fetch(finalUrl, {
                        ...options,
                        headers,
                        // AIDEV-NOTE: SSR login sets auth cookies before the browser
                        // Supabase client finishes hydrating an access token. Always
                        // include cookies so /api/user and /api/session stay authenticated.
                        credentials: options.credentials ?? "include",
                    });

                    // AIDEV-NOTE: Our API never returns HTML for 429s. 429 + text/html = Vercel WAF.
                    // Clone only on 429+HTML (rare) so the original stream stays consumable by callers.
                    const contentType =
                        response.headers.get("content-type") || "";
                    if (
                        response.status === 429 &&
                        contentType.toLowerCase().includes("text/html")
                    ) {
                        const cloned = response.clone();
                        const body = await cloned.text();
                        const isVercelWaf = body.includes(
                            "Vercel Security Checkpoint",
                        );
                        logger
                            .withContext({
                                url,
                                finalUrl,
                                method: options.method || "GET",
                                status: response.status,
                                contentType,
                                isVercelWaf,
                            })
                            .error(
                                isVercelWaf
                                    ? "Vercel WAF block detected"
                                    : "429 with HTML response (unknown source)",
                            );
                        captureException(
                            isVercelWaf
                                ? "Vercel WAF block"
                                : "Unknown 429 with HTML response",
                            new Error(
                                isVercelWaf
                                    ? `Vercel WAF block: ${
                                          options.method || "GET"
                                      } ${url}`
                                    : `429 HTML response: ${
                                          options.method || "GET"
                                      } ${url} — body: ${
                                          new DOMParser()
                                              .parseFromString(
                                                  body,
                                                  "text/html",
                                              )
                                              .body?.textContent?.trim()
                                              .slice(0, 2000) ??
                                          body.slice(0, 2000)
                                      }`,
                            ),
                        );
                    }

                    // Successfully got a response (even if it's an HTTP error like 4xx/5xx)
                    // Return it - HTTP error handling is the caller's responsibility
                    return response;
                } catch (error) {
                    lastError =
                        error instanceof Error
                            ? error
                            : new Error(String(error));

                    // Only retry on network errors, not other errors (e.g., AbortError)
                    if (!isRetryableNetworkError(error)) {
                        logger
                            .withContext({
                                url,
                                method: options.method || "GET",
                                error: lastError.message,
                                attempt,
                            })
                            .warn(
                                "API request failed with non-retryable error",
                            );
                        throw lastError;
                    }

                    // If this was the last attempt, don't log retry warning
                    if (attempt < MAX_RETRIES) {
                        const retryDelay =
                            INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
                        logger
                            .withContext({
                                url,
                                method: options.method || "GET",
                                error: lastError.message,
                                attempt,
                                maxRetries: MAX_RETRIES,
                                retryDelayMs: retryDelay,
                                online: isNetworkAvailable(),
                            })
                            .warn(
                                `API request failed, retrying (attempt ${attempt}/${MAX_RETRIES})`,
                            );
                        await sleep(retryDelay);
                    }
                }
            }

            // All retries exhausted
            const enhancedError = new Error(
                `API request failed after ${MAX_RETRIES} attempts: ${
                    lastError?.message || "Unknown error"
                } [${options.method || "GET"} ${url}]`,
            );
            logger
                .withContext({
                    url,
                    method: options.method || "GET",
                    finalError: lastError?.message,
                    online: isNetworkAvailable(),
                })
                .error("API request failed after all retry attempts");
            throw enhancedError;
        },
        [accessToken, buildUrl],
    );

    return useMemo(
        () => ({
            get: (url: string, options: RequestParams = {}) =>
                request(url, { method: "GET", ...options }),
            post: (
                url: string,
                body?: Record<string, unknown>,
                options: RequestParams = {},
            ) =>
                request(url, {
                    method: "POST",
                    body: JSON.stringify(body),
                    ...options,
                }),
            put: (
                url: string,
                body?: Record<string, unknown>,
                options: RequestParams = {},
            ) =>
                request(url, {
                    method: "PUT",
                    body: JSON.stringify(body),
                    ...options,
                }),
            patch: (
                url: string,
                body?: Record<string, unknown>,
                options: RequestParams = {},
            ) =>
                request(url, {
                    method: "PATCH",
                    body: JSON.stringify(body),
                    ...options,
                }),
            // AIDEV-NOTE: Body is OPT-IN through the options bag, deliberately NOT a second
            // positional argument like post/put/patch. Existing callers already pass options
            // second (useDeletePartImage sends `{ params: { partId } }`), so a positional body
            // would silently serialize their query params as JSON and drop them from the URL.
            delete: (
                url: string,
                options: RequestParams & {
                    body?: Record<string, unknown>;
                } = {},
            ) => {
                const { body, ...rest } = options;
                return request(url, {
                    method: "DELETE",
                    ...(body === undefined
                        ? {}
                        : { body: JSON.stringify(body) }),
                    ...rest,
                });
            },
            loading,
        }),
        [request, loading],
    );
};
